import * as React from "react"
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react"

import { cn } from "@/lib/utils"
import { ButtonProps, buttonVariants, Button } from "@/components/ui/button"

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
)
Pagination.displayName = "Pagination"

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex flex-row items-center gap-1", className)}
    {...props}
  />
))
PaginationContent.displayName = "PaginationContent"

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
))
PaginationItem.displayName = "PaginationItem"

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, "size"> &
  React.ComponentProps<"a">

const PaginationLink = React.forwardRef<HTMLAnchorElement, PaginationLinkProps>(
  ({ className, isActive, size = "icon", ...props }, ref) => (
    <a
      ref={ref}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        buttonVariants({
          variant: isActive ? "outline" : "ghost",
          size,
        }),
        isActive && "bg-accent",
        className
      )}
      {...props}
    />
  )
)
PaginationLink.displayName = "PaginationLink"

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="default"
    className={cn("gap-1 pl-2.5", className)}
    {...props}
  >
    <ChevronLeft className="h-4 w-4" />
    <span>Previous</span>
  </PaginationLink>
)
PaginationPrevious.displayName = "PaginationPrevious"

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    size="default"
    className={cn("gap-1 pr-2.5", className)}
    {...props}
  >
    <span>Next</span>
    <ChevronRight className="h-4 w-4" />
  </PaginationLink>
)
PaginationNext.displayName = "PaginationNext"

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = "PaginationEllipsis"

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}

// Custom pagination component for easy use
type CustomPaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  siblingCount?: number;
}

export function CustomPagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
}: CustomPaginationProps) {
  const generatePagination = () => {
    // Always show first and last page
    const firstPage = 1;
    const lastPage = totalPages;
    
    // Pages to show directly
    const pagesBeforeCurrent = Math.max(0, siblingCount);
    const pagesAfterCurrent = Math.max(0, siblingCount);
    
    // Calculate the range
    let startPage = Math.max(firstPage, currentPage - pagesBeforeCurrent);
    let endPage = Math.min(lastPage, currentPage + pagesAfterCurrent);
    
    // Adjust if we're at the beginning or end
    if (startPage === firstPage) {
      endPage = Math.min(lastPage, firstPage + siblingCount * 2);
    }
    
    if (endPage === lastPage) {
      startPage = Math.max(firstPage, lastPage - siblingCount * 2);
    }
    
    // Generate the array of pages
    const pages = Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
    
    // Add first page if needed
    if (startPage > firstPage) {
      if (startPage > firstPage + 1) {
        pages.unshift(-1); // -1 represents ellipsis
      }
      pages.unshift(firstPage);
    }
    
    // Add last page if needed
    if (endPage < lastPage) {
      if (endPage < lastPage - 1) {
        pages.push(-1); // -1 represents ellipsis
      }
      pages.push(lastPage);
    }
    
    return pages;
  };
  
  const handlePageChange = (page: number) => {
    if (onPageChange && typeof onPageChange === 'function') {
      onPageChange(page);
    }
  };
  
  // Fix để đảm bảo currentPage luôn ít nhất là 1
  const validCurrentPage = Math.max(1, currentPage);
  
  const pages = generatePagination();
  
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      className="mx-auto flex w-full justify-center"
    >
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={(e) => {
              e.preventDefault();
              if (validCurrentPage > 1) {
                handlePageChange(validCurrentPage - 1);
              }
            }}
            className={validCurrentPage <= 1 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
        
        {pages.map((page, index) => (
          <PaginationItem key={index}>
            {page === -1 ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                isActive={page === validCurrentPage}
                onClick={(e) => {
                  e.preventDefault();
                  handlePageChange(page);
                }}
              >
                {page}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        
        <PaginationItem>
          <PaginationNext
            onClick={(e) => {
              e.preventDefault();
              if (validCurrentPage < totalPages) {
                handlePageChange(validCurrentPage + 1);
              }
            }}
            className={validCurrentPage >= totalPages ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
      </PaginationContent>
    </nav>
  );
}

// ***KHÔNG*** re-export với tên Pagination để tránh conflict với component Pagination gốc 